import {
  Autocomplete,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  type FC,
  type SyntheticEvent,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { useDynamicFormsTranslate, useSelectItemsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  Log,
  noop,
  ValidationHelpers,
  type ICustomField,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";
import {
  DropdownIconRenderer,
  DropdownOptionRenderer,
  getDropdownValuesLabels,
  ListBox,
  useInfluencedDropdownTemplate,
  useTechnicalNameOptions,
} from "./dropdowns";

interface IDropdownTemplateProps extends ICustomField {
  size?: "small" | "medium";
  formValueToDropdownValue?: (val: unknown) => unknown;
  dropdownValueToFormValue?: (val: unknown) => unknown;
  onBlur?: (formOnBlur: () => void) => void;
  onSelect?: (value: unknown) => void;
}

export const DropdownTemplate: FC<IDropdownTemplateProps> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  size = "medium",
  formValueToDropdownValue,
  dropdownValueToFormValue,
  e2eData = "dropdown-template",
  onBlur,
  onSelect,
  labelTranslated,
  uniqueAttrId,
}) => {
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const generatedId = useId();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const {
    selectItems,
    disabled,
    boldValues,
    italicValues,
    optionIconMapping,
    disabledValues,
    skipDropdownItemsTranslation,
    translationPath,
  } = formAttributes || {};

  useInfluencedDropdownTemplate({ formAttributes, name });

  const { translateLabel } = useSelectItemsTranslate();

  const [options, mapOptionLabel] = useMemo(() => {
    return getDropdownValuesLabels({
      dropDownItems: selectItems,
      transformLabelFn: skipDropdownItemsTranslation
        ? undefined
        : ({ label }) => translateLabel(label, translationPath),
    });
  }, [
    selectItems,
    skipDropdownItemsTranslation,
    translateLabel,
    translationPath,
  ]);

  const getOptionLabel = useCallback(
    (value: unknown) => String(mapOptionLabel.get(value as string | number)),
    [mapOptionLabel],
  );

  const { technicalNames, filterOptions } = useTechnicalNameOptions({
    selectItems,
    getOptionLabel,
  });

  const requiredComputed = useMemo(
    () => ValidationHelpers.hasRequiredRule(formValidation),
    [formValidation],
  );

  useEffect(() => {
    if (!requiredComputed) {
      clearErrors(name);
    }
  }, [requiredComputed, clearErrors, name]);

  const onChangeDropdown = useEvent(
    (
      e: SyntheticEvent<Element, Event>,
      newValue: string | number | boolean | null,
    ): void => {
      const valueNormalized = dropdownValueToFormValue
        ? dropdownValueToFormValue(newValue)
        : newValue;
      Log.debug("Dropdown template value changed to:", valueNormalized);
      setValue(name, valueNormalized, {
        shouldDirty: true,
        shouldValidate: true,
      });
      onSelect?.(valueNormalized);
    },
  );

  const inputId = uniqueAttrId ?? `${name}-${generatedId}`;

  return (
    <FullWidthTextFieldWrapper>
      <Controller
        control={control}
        name={name}
        render={({
          field: { value: formValue, onChange: _fieldOnChange, ...restField },
          fieldState: { error },
        }) => {
          const valueNormalized = formValueToDropdownValue
            ? formValueToDropdownValue(formValue)
            : formValue;

          return (
            <FormControl
              data-e2e-template="dropdown-template"
              data-e2e-field-name={name}
              required={requiredComputed}
              fullWidth
              error={!!error}
            >
              <Autocomplete
                id={inputId}
                {...restField}
                data-e2e={e2eData}
                data-e2e-id={`${name}_${id || ""}`}
                options={options || []}
                filterOptions={filterOptions}
                renderOption={(props, option) =>
                  DropdownOptionRenderer({
                    listItemProps: props,
                    option: option as string | number,
                    getOptionLabel,
                    boldValues,
                    italicValues,
                    disabledValues,
                    optionIconMapping,
                    technicalNames,
                  })
                }
                value={valueNormalized ?? null}
                slotProps={{
                  listbox: {
                    component: ListBox,
                  },
                }}
                size={size}
                disabled={disabled}
                onChange={(event, data) => {
                  checkCleanupNestedValidation(name, errors);
                  onChangeDropdown(event, data as string | boolean | number);
                }}
                onBlur={() => {
                  if (onBlur) {
                    onBlur(restField?.onBlur ?? noop);
                  } else {
                    restField?.onBlur?.();
                  }
                }}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                getOptionLabel={getOptionLabel}
                renderInput={params => (
                  <TextField
                    id={params.id}
                    disabled={disabled}
                    fullWidth={params.fullWidth}
                    size={params.size}
                    slotProps={{
                      inputLabel: params.slotProps.inputLabel,
                      htmlInput: params.slotProps.htmlInput,
                      input: {
                        ...params.slotProps.input,
                        ...(valueNormalized && optionIconMapping?.values
                          ? {
                              startAdornment: (
                                <>
                                  <InputAdornment
                                    position="start"
                                    sx={{ m: 0 }}
                                  >
                                    {DropdownIconRenderer(
                                      valueNormalized,
                                      optionIconMapping,
                                    )}
                                  </InputAdornment>
                                  {params.slotProps.input.startAdornment}
                                </>
                              ),
                            }
                          : {}),
                      },
                    }}
                    error={!!error}
                    label={formLabelTranslated}
                    required={requiredComputed}
                    data-e2e="dropdown-template-input"
                  />
                )}
              />
              {error && (
                <FormHelperText data-e2e="dropdown-template-error">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
      <FieldExplanationIcon formAttributes={formAttributes} />
    </FullWidthTextFieldWrapper>
  );
};
